import type { Fundamentals, HistoricalSeries, YearValue } from "@/lib/data/types";
import { medianOf, averageOf } from "@/lib/stats";
import { formatCompactCurrency, formatPercent, formatRatio } from "@/lib/format";

export type GoodDirection = "lower" | "higher";
export type FormatKind = "ratio" | "percent" | "currency";
export type KpiCategory =
  | "bewertung"
  | "profitabilitaet"
  | "dividende"
  | "finanzielle-gesundheit";

// Reine Daten, keine Funktionsfelder: KpiDefinition wird teils an Client Components
// weitergereicht, und Server->Client darf laut RSC keine Funktionen serialisieren.
export interface KpiDefinition {
  key: keyof Fundamentals;
  label: string;
  tooltip: string;
  formatKind: FormatKind;
  goodDirection: GoodDirection;
  category: KpiCategory;
  /** null = keine Zeitreihe verfuegbar (z.B. Verschuldungskennzahlen). */
  historicalSeriesKey: keyof HistoricalSeries | null;
  /** 'median' fuer volatile Bewertungskennzahlen (KGV), sonst Durchschnitt. */
  historicalAggregate: "median" | "average";
}

export function formatKpiValue(
  kind: FormatKind,
  value: number | undefined,
  currency: string | undefined,
): string {
  switch (kind) {
    case "percent":
      return formatPercent(value);
    case "currency":
      return formatCompactCurrency(value, currency);
    default:
      return formatRatio(value);
  }
}

export function getHistoricalSeries(def: KpiDefinition, h: HistoricalSeries): YearValue[] {
  return def.historicalSeriesKey ? h[def.historicalSeriesKey] : [];
}

export const KPI_DEFINITIONS: KpiDefinition[] = [
  {
    key: "pe",
    label: "KGV (P/E)",
    tooltip: "Kurs-Gewinn-Verhältnis: Aktienkurs geteilt durch Gewinn je Aktie.",
    formatKind: "ratio",
    goodDirection: "lower",
    category: "bewertung",
    historicalSeriesKey: "pe",
    historicalAggregate: "median",
  },
  {
    key: "pb",
    label: "KBV (P/B)",
    tooltip: "Kurs-Buchwert-Verhältnis: Aktienkurs geteilt durch bilanzielles Eigenkapital je Aktie.",
    formatKind: "ratio",
    goodDirection: "lower",
    category: "bewertung",
    historicalSeriesKey: "pb",
    historicalAggregate: "median",
  },
  {
    key: "dividendYield",
    label: "Dividendenrendite",
    tooltip: "Jährliche Dividende geteilt durch den aktuellen Aktienkurs.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "dividende",
    historicalSeriesKey: "dividendYield",
    historicalAggregate: "average",
  },
  {
    key: "grossMargin",
    label: "Bruttomarge",
    tooltip: "Anteil des Umsatzes, der nach Herstellungskosten übrig bleibt.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "profitabilitaet",
    historicalSeriesKey: "grossMargin",
    historicalAggregate: "average",
  },
  {
    key: "operatingMargin",
    label: "Operative Marge",
    tooltip: "Anteil des Umsatzes, der nach operativen Kosten (vor Zinsen und Steuern) übrig bleibt - zeigt die Profitabilität des Kerngeschäfts ohne Finanzierungs-/Steuereffekte.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "profitabilitaet",
    // backlog.md [Punkt 44]: jetzt echte Zeitreihe statt Einzelwert - eine aeltere Notiz
    // hier hatte angenommen, Yahoos Timeseries-Endpunkt liefere kein
    // "annualOperatingIncome"-Aequivalent; live gegen AAPL widerlegt (siehe
    // getHistorical() in yahoo-provider.ts), zusaetzlich ueber SEC-EDGAR (US-Filer) und
    // Alpha Vantage (nicht-US) erweitert.
    historicalSeriesKey: "operatingMargin",
    historicalAggregate: "average",
  },
  {
    key: "netMargin",
    label: "Nettomarge",
    tooltip: "Anteil des Umsatzes, der als Gewinn übrig bleibt.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "profitabilitaet",
    historicalSeriesKey: "netMargin",
    historicalAggregate: "average",
  },
  {
    key: "totalDebt",
    label: "Gesamtverschuldung",
    tooltip:
      "Summe aller verzinslichen Verbindlichkeiten, Stand letzter verfügbarer Bilanz (Quartals- oder Jahresbericht) - kann daher vom letzten Punkt der Verschuldungs-Verlaufsgrafik oben (immer Geschäftsjahresende) abweichen, wenn seitdem ein neuerer Quartalsbericht erschienen ist.",
    formatKind: "currency",
    goodDirection: "lower",
    category: "finanzielle-gesundheit",
    historicalSeriesKey: null,
    historicalAggregate: "average",
  },
  {
    key: "netDebt",
    label: "Nettoverschuldung",
    tooltip: "Gesamtverschuldung abzüglich verfügbarer liquider Mittel, Stand letzter verfügbarer Bilanz.",
    formatKind: "currency",
    goodDirection: "lower",
    category: "finanzielle-gesundheit",
    historicalSeriesKey: null,
    historicalAggregate: "average",
  },
  {
    key: "debtToEquity",
    label: "Schuldenquote (Verschuldungsgrad D/E)",
    tooltip: "Verzinsliche Schulden im Verhältnis zum bilanziellen Eigenkapital.",
    formatKind: "percent",
    goodDirection: "lower",
    category: "finanzielle-gesundheit",
    historicalSeriesKey: null,
    historicalAggregate: "average",
  },
  {
    key: "roic",
    label: "ROIC (Näherung)",
    tooltip: "Rendite auf das investierte Kapital - je höher, desto effizienter das Geschäftsmodell.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "profitabilitaet",
    historicalSeriesKey: "roic",
    historicalAggregate: "average",
  },
  {
    key: "roa",
    label: "Gesamtkapitalrendite (ROA)",
    tooltip: "Nettogewinn im Verhältnis zur Bilanzsumme - wie effizient das Unternehmen mit seinem Vermögen wirtschaftet.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "profitabilitaet",
    // backlog.md [Punkt 44]: jetzt echte Zeitreihe statt Einzelwert, siehe operatingMargin
    // oben fuer den Hintergrund.
    historicalSeriesKey: "roa",
    historicalAggregate: "average",
  },
  {
    key: "roe",
    label: "Eigenkapitalrendite (ROE)",
    tooltip: "Nettogewinn im Verhältnis zum bilanziellen Eigenkapital.",
    formatKind: "percent",
    goodDirection: "higher",
    category: "profitabilitaet",
    historicalSeriesKey: "roe",
    historicalAggregate: "average",
  },
  {
    key: "priceToSales",
    label: "Kurs-Umsatz-Verhältnis (KUV)",
    tooltip: "Marktkapitalisierung im Verhältnis zum Jahresumsatz - nützlich bei Unternehmen ohne stabilen Gewinn.",
    formatKind: "ratio",
    goodDirection: "lower",
    category: "bewertung",
    historicalSeriesKey: "priceToSales",
    historicalAggregate: "average",
  },
  {
    key: "evToEbitda",
    label: "EV/EBITDA",
    tooltip: "Unternehmenswert (Marktkapitalisierung plus Nettoschulden) im Verhältnis zum operativen Ergebnis vor Zinsen, Steuern und Abschreibungen.",
    formatKind: "ratio",
    goodDirection: "lower",
    category: "bewertung",
    historicalSeriesKey: null,
    historicalAggregate: "average",
  },
];

export function computeHistoricalBaseline(def: KpiDefinition, h: HistoricalSeries) {
  const series = getHistoricalSeries(def, h);
  return def.historicalAggregate === "median" ? medianOf(series) : averageOf(series);
}

/**
 * Beschriftung fuer den historischen Referenzwert, basierend auf der tatsaechlich
 * verfuegbaren Anzahl Jahre statt eines fest verdrahteten "10-J" - die freien
 * Datenquellen liefern für Per-Share-Kennzahlen (KGV, KBV) je nach Ticker oft nur
 * 5-8 Jahre (Aktiensplits kappen die SEC-Ergaenzung), nie verlaesslich 10.
 */
export function historicalPeriodLabel(def: KpiDefinition, series: YearValue[]): string {
  const kind = def.historicalAggregate === "median" ? "Median" : "Ø";
  return series.length > 0 ? `${series.length}-J-${kind}` : kind;
}

export type DeltaTone = "positive" | "negative" | "neutral";

export function deltaTone(
  current: number | undefined,
  baseline: number | undefined,
  direction: GoodDirection,
): DeltaTone {
  if (current === undefined || baseline === undefined || baseline === 0) return "neutral";
  const ratio = current / baseline;
  const threshold = 0.03; // < 3% Abweichung gilt als neutral
  if (Math.abs(ratio - 1) < threshold) return "neutral";
  const isHigher = ratio > 1;
  if (direction === "higher") return isHigher ? "positive" : "negative";
  return isHigher ? "negative" : "positive";
}
